/**
 * Discounts Tab
 *
 * Displays contract discounts
 */

import { Loader2, Tag } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { useContractDiscounts } from '../../hooks/useContracts'

interface DiscountsTabProps {
  contractId: string
}

export function DiscountsTab({ contractId }: DiscountsTabProps) {
  const { data: discounts, isLoading, error } = useContractDiscounts(contractId)

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground'>Loading discounts...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <p className='text-red-600 font-medium'>Failed to load discounts</p>
          <p className='text-sm text-muted-foreground mt-1'>{error.message}</p>
        </div>
      </div>
    )
  }

  if (!discounts || discounts.length === 0) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='text-center'>
          <Tag className='h-12 w-12 mx-auto mb-4 text-muted-foreground' />
          <p className='text-muted-foreground font-medium'>No discounts</p>
          <p className='text-sm text-muted-foreground mt-1'>
            This contract has no discounts applied
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      {discounts.map(discount => (
        <div key={discount.id} className='border rounded-lg p-4 space-y-3'>
          <div className='flex items-start justify-between'>
            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <h4 className='font-medium'>{discount.name}</h4>
                <Badge variant='outline'>
                  {discount.type === 'percentage'
                    ? `${discount.percentage_off ?? 0}%`
                    : `${discount.amount_off ?? 0} off`}
                </Badge>
              </div>
              {discount.description && (
                <p className='text-sm text-muted-foreground'>
                  {discount.description}
                </p>
              )}
            </div>
          </div>

          {/* Discount details */}
          <div className='grid grid-cols-2 gap-3 text-sm'>
            {discount.type && (
              <div>
                <span className='text-muted-foreground'>Type:</span>{' '}
                <span className='font-medium capitalize'>{discount.type}</span>
              </div>
            )}
            {discount.applies_to && (
              <div>
                <span className='text-muted-foreground'>Applies To:</span>{' '}
                <span className='font-medium capitalize'>
                  {discount.applies_to}
                </span>
              </div>
            )}
            {discount.valid_until && (
              <div>
                <span className='text-muted-foreground'>Valid Until:</span>{' '}
                <span className='font-medium'>{discount.valid_until}</span>
              </div>
            )}
          </div>

          {/* Additional metadata */}
          {discount.metadata && Object.keys(discount.metadata).length > 0 && (
            <div className='border-t pt-3'>
              <div className='text-xs text-muted-foreground'>
                <pre className='overflow-auto'>
                  {JSON.stringify(discount.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
